

























































































































































































import Vue from '@front-app/vendor/vue';
import {
	computed,
	defineComponent,
	nextTick,
	Ref,
	ref,
	PropType,
} from '@vue/composition-api';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useConfirmDialog } from '@vueuse/core';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import FollowButton from '@front-app/components/buttons/concrete/FollowButton.vue';
import PlusButton from '@front-app/components/buttons/PlusButton.vue';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { Size, Spacing } from '@front-app/components/constants';
import ConfirmationModal from '@front-app/components/modals/ConfirmationModal.vue';
import LoaderState from '@front-app/components/LoaderState.vue';
import {
	Content,
	ContentType,
	ContentV2,
	DiscussionMessage,
	ReactionType,
} from '@front-app/services/GroupsApi/DiscussionModel';
import { GroupWithCommunity } from '@front-app/services/GroupsApi/GroupModel';
import { deleteContentFromDiscussion } from '@front-app/services/GroupsApi/GroupsDiscussionApi';
import { ContentQueryParam } from '@front-app/modules/content/routing/params';
import { CurrentUserStore } from '@front-app/store/singletons/User';
import { routeToShareContent, ShareContentPanelState } from '@front-app/modules/group/util/shareContentModal';
import {
	canShareFilesToGroup,
	canUserReplyToDiscussion,
} from '@front-app/util/permission';
import { canSeeRemovedDiscussionMessage } from '@front-app/util/permission/discussion';
import { checkIfEmbeddedVideo } from '@front-app/components/video/util';
import { isNotNullish } from '@front-app/util/types';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { useDiscussionStore } from '@front-app/store/piniaModules/discussions';
import { useDiscussionMessageStore } from '@front-app/store/piniaModules/discussionMessages';
import { useGroupContentStore } from '@front-app/store/piniaModules/groupContent';
import LinkContentCard from '@front-app/components/cards/content/LinkContentCard.vue';
import FileContentCard from '@front-app/components/cards/content/FileContentCard.vue';
import DiscussionMessageCard from '@front-app/components/cards/discussion/DiscussionMessageCard.vue';
import StandardErrorText from '@front-app/components/cards/state/StandardErrorText.vue';
import { getContentDomId, isNeedRefreshError } from './util';
import { MessageState } from './Model';
import CategoryButton from '../buttons/concrete/CategoryButton.vue';
import SimpleCard from '../cards/SimpleCard.vue';
import DiscussionForm from './DiscussionForm.vue';
import IconButton from '../buttons/IconButton.vue';
import EditDiscussionPostPanel from './EditDiscussionPostPanel.vue';
import WarningCard from '../cards/state/WarningCard.vue';

const CONTENT_CARD_BY_TYPE = {
	[ContentType.DISCUSSION]: DiscussionMessageCard,
	[ContentType.LINK]: LinkContentCard,
	[ContentType.FILE]: FileContentCard,
};

const CONTENT_INFO_PANEL_ARIA_LABEL_BY_TYPE = {
	[ContentType.FILE]: 'discussion.aria.open-file-information-panel',
	[ContentType.LINK]: 'discussion.aria.open-link-information-panel',
	[ContentType.DISCUSSION]: 'discussion.aria.open-discussion',
};

const isMessage = (item: Content | ContentV2 | DiscussionMessage): item is DiscussionMessage => Object.prototype.hasOwnProperty.call(item, 'read');

export default defineComponent({
	components: {
		CategoryButton,
		ConfirmationModal,
		CtaButton,
		DiscussionForm,
		EditDiscussionPostPanel,
		FollowButton,
		FontAwesomeIcon,
		IconButton,
		LoaderState,
		PlusButton,
		SimpleCard,
		StandardErrorText,
		WarningCard,
	},
	props: {
		group: {
			type: Object as PropType<GroupWithCommunity>,
			required: true,
		},
		discussionId: {
			type: String,
			required: true,
		},
		canEditCategories: Boolean,
		canEditSubject: Boolean,
	},
	setup(props, ctx) {
		const discussionStore = useDiscussionStore();
		const discussionMessageStore = useDiscussionMessageStore();
		const content = computed(() => discussionStore.getById(props.discussionId));

		const isContentItemVisible = (contentItem: DiscussionMessage | ContentV2) => {
			if (!isMessage(contentItem)) {
				return true;
			}
			return !contentItem.removedAt || canSeeRemovedDiscussionMessage(contentItem);
		};

		const messagesAndAttachments = computed(() => content
			.value
			?.metadata
			.discussion
			.messages
			.map(discussionMessageStore.getById)
			.filter(isNotNullish)
			.flatMap((message) => [message,	...message.attachments.contents])
			.filter(isContentItemVisible) || []);
		const numPosts = computed(() => messagesAndAttachments.value?.length);

		// New posts
		const firstNewIndex = computed(() => {
			const theMessages = messagesAndAttachments.value;
			return theMessages.findIndex((item) => {
				if (isMessage(item)) {
					return !item.read;
				}
				return false;
			});
		});
		const firstNewEl: Ref<null | Vue[]> = ref(null);
		const scrollToNewPosts = async () => {
			await nextTick();
			const val = firstNewEl.value;
			if (!val || val.length < 1) {
				return;
			}
			const el = val[0].$el as HTMLButtonElement;
			el.scrollIntoView({ block: 'center' });
			el.focus();
		};
		// END New posts

		// Attach content
		const toShareContent = computed(() => {
			const discussionMessages = content.value?.metadata.discussion.messages;
			let shareContentMessageId;
			if (discussionMessages) {
				shareContentMessageId = discussionMessages[discussionMessages?.length - 1];
			} else {
				shareContentMessageId = props.discussionId;
			}
			return routeToShareContent(ctx, ShareContentPanelState.FILE, shareContentMessageId);
		});
		// END Attach content

		// Follow
		const isFollowing = computed(() => useDiscussionStore().isFollowing(props.discussionId));
		const {
			isLoading: isSubmittingFollow,
			track: trackToggleFollow,
		} = useAsyncStatus();
		const performFollowRequest = async () => trackToggleFollow(useDiscussionStore().toggleFollow(props.discussionId));
		// END Follows

		// Replies
		const canReply = computed(() => canUserReplyToDiscussion(props.group, CurrentUserStore.isCsr.value));
		const {
			track: trackReply,
			isSuccess: isReplySuccess,
			isError: hasReplyError,
			status: createReplyStatus,
		} = useAsyncStatus();
		const isRefreshError = ref(false);
		const createReplyToDiscussion = async (messageState: MessageState) => {
			isRefreshError.value = false;
			try {
				await trackReply(discussionStore.reply(props.group.id, props.discussionId, messageState));
				if (isReplySuccess.value) {
					// We pass isFollowing value to the parent component GroupDiscussion, which it also has access to,
					// in order to avert a potential race condition between the API request and re-render with an updated isFollowing value
					ctx.emit('display-snackbar', isFollowing.value);
					// Follow the discussion if not previously followed
					if (!isFollowing.value) {
						performFollowRequest();
					}
				}
			} catch (e) {
				if (isNeedRefreshError(e)) {
					isRefreshError.value = true;
				} else {
					return Promise.reject(e);
				}
			}
			return Promise.resolve();
		};
		// END Replies

		// Edit
		const selectedMessageIdToEdit: Ref<null | string> = ref(null);
		const editModal = useConfirmDialog();
		const beginEdit = (contentItem: ContentV2) => {
			if (contentItem.type === ContentType.FILE || contentItem.type === ContentType.LINK) {
				const q = ctx.root.$route.query;
				ctx.root.$router.push({ query: { ...q, [ContentQueryParam.EDIT_CONTENT]: contentItem.id } });
			} else {
				selectedMessageIdToEdit.value = contentItem.id;
				editModal.reveal();
			}
		};
		// END Edit

		// Delete
		const deleteContentModal = useConfirmDialog();
		const contentSelectedForDeletion: Ref<ContentV2 | null> = ref(null);
		const showDeleteContentModal = async (contentForDeletion: ContentV2) => {
			if (contentForDeletion.type === ContentType.DISCUSSION) {
				return;
			}
			contentSelectedForDeletion.value = contentForDeletion;
			deleteContentModal.reveal();
		};
		const {
			track: trackContentDelete,
			isLoading: isDeletingContent,
			isError: errorDeletingContent,
			isSuccess: isSuccessDeletingContent,
		} = useAsyncStatus();
		const performContentDeletion = async () => {
			if (!contentSelectedForDeletion.value || contentSelectedForDeletion.value.type === ContentType.DISCUSSION) {
				return trackContentDelete(Promise.reject());
			}

			// Find the id of the parent text comment (DiscussionMessage) to which the content is attached
			const parentMessageId = discussionMessageStore.getAttachmentParentId(contentSelectedForDeletion.value.id);
			if (!parentMessageId) {
				return trackContentDelete(Promise.reject());
			}

			await trackContentDelete(deleteContentFromDiscussion(
				props.group.id,
				parentMessageId,
				contentSelectedForDeletion.value.id,
			));

			if (isSuccessDeletingContent) {
				deleteContentModal.confirm();
				contentSelectedForDeletion.value = null;
				// Refresh the discussion messages list
				discussionStore.fetch(props.group.id, props.discussionId);
				return Promise.resolve();
			}
			return trackContentDelete(Promise.reject());
		};
		// END Delete

		// Reactions
		const groupContentStore = useGroupContentStore();
		const reactToContent = async (contentType: ContentType, contentId: string, reactionType: ReactionType) => {
			await groupContentStore.react(props.group.id, contentType, contentId, reactionType);
		};
		// END Reactions

		return {
			CONTENT_CARD_BY_TYPE,
			CONTENT_INFO_PANEL_ARIA_LABEL_BY_TYPE,
			// Discussion list
			numPosts,
			content,
			messagesAndAttachments,
			// New items
			firstNewIndex,
			firstNewEl,
			scrollToNewPosts,
			// Sharing
			canShareFilesToGroup,
			toShareContent,
			// Follows
			isFollowing,
			isSubmittingFollow,
			performFollowRequest,
			// Replies
			canReply,
			isRefreshError,
			hasReplyError,
			createReplyStatus,
			createReplyToDiscussion,
			// Edit
			selectedMessageIdToEdit,
			editModal,
			beginEdit,
			// Delete
			deleteContentModal,
			contentSelectedForDeletion,
			showDeleteContentModal,
			isDeletingContent,
			errorDeletingContent,
			performContentDeletion,
			// Reactions
			reactToContent,
			// Util
			checkIfEmbeddedVideo,
			getContentDomId,
			ButtonPalettes,
			Size,
			Spacing,
			ContentType,
		};
	},
});
